import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import queryString from 'query-string'
import { isEmpty, isNil } from 'lodash'

import { Button } from '@/components/buttons/Button'
import { Checkbox } from '@/components/forms/Checkbox'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { Loader } from '@/components/loaders/Loader'
import { clearSavedSession } from '@/reducers/auth/currentUser.actions'
import { formatTime } from '@/utils/helpers'
import { AuthContainer } from '@/views/Auth/AuthContainer'

import {
  OAUTH_ERROR,
  OAUTH_SCOPE,
  fetchOAuthRequest,
  signOutSavedUser,
  submitOAuthDecision,
} from './oauthConsent.api'

import './OAuthConsentPage.scss'

const EXPIRY_DATE_FORMAT = 'MMMM D, YYYY'

const DECISION = {
  ALLOW: 'allow',
  DENY: 'deny',
}

const ERROR_MESSAGES = {
  [OAUTH_ERROR.INVALID_LINK]: "This connection link isn't valid. Go back to the app and try again.",
  [OAUTH_ERROR.REQUEST_NOT_FOUND]:
    'This request has expired or was already used. Go back to the app and connect again.',
  [OAUTH_ERROR.INTERNAL_ACCOUNT]:
    "Balto internal accounts can't connect apps. Sign in with a customer account.",
  [OAUTH_ERROR.OAUTH_DISABLED]:
    'Your organization has turned off connected apps. Ask your Balto admin to turn them on.',
  [OAUTH_ERROR.UNKNOWN]: 'Something went wrong. Try again.',
}

// These end the request for good, so the page shows only the message instead of the form.
const PAGE_ERRORS = [
  OAUTH_ERROR.REQUEST_NOT_FOUND,
  OAUTH_ERROR.INTERNAL_ACCOUNT,
  OAUTH_ERROR.OAUTH_DISABLED,
]

// Signing in as someone else can get past these, so the page offers it.
const ACCOUNT_ERRORS = [OAUTH_ERROR.INTERNAL_ACCOUNT, OAUTH_ERROR.OAUTH_DISABLED]

// currentUser is only hydrated on protected routes, so read the session the login page saved.
const readSavedUser = () => JSON.parse(localStorage.getItem('User'))

export const OAuthConsentPage = () => {
  const history = useHistory()
  const location = useLocation()
  const { request: requestId, error: linkError } = queryString.parse(location.search)
  const [savedUser] = useState(readSavedUser)
  const [consentRequest, setConsentRequest] = useState(null)
  const [pageError, setPageError] = useState(null)
  const [decisionError, setDecisionError] = useState(null)
  const [allowWrite, setAllowWrite] = useState(true)
  const [pendingDecision, setPendingDecision] = useState(null)
  const [switchingAccount, setSwitchingAccount] = useState(false)

  const invalidLink = !isEmpty(linkError) || isEmpty(requestId)

  const sendToLogin = () => {
    history.replace(
      `/login?${queryString.stringify({ next: `${location.pathname}${location.search}` })}`
    )
  }

  const loadRequest = () => {
    fetchOAuthRequest(requestId, savedUser).then(({ data, error }) => {
      if (isNil(error)) {
        setConsentRequest(data)
      } else if (error.kind === OAUTH_ERROR.SIGNED_OUT) {
        sendToLogin()
      } else {
        setPageError(error)
      }
    })
  }

  useEffect(() => {
    if (invalidLink) {
      return
    }

    if (isEmpty(savedUser?.token)) {
      sendToLogin()
      return
    }

    loadRequest()
  }, [])

  const handleRetry = () => {
    setPageError(null)
    loadRequest()
  }

  // Signs out and comes back to this request after the next login.
  const handleSwitchAccount = async () => {
    setSwitchingAccount(true)
    await signOutSavedUser(savedUser)
    clearSavedSession()
    sendToLogin()
  }

  // Access is granted as whoever is signed in, so say who that is and offer a way out.
  const renderAccount = () => (
    <p className="muted-text" data-testid="oauth-account">
      Signed in as {savedUser.username}.{' '}
      <Button
        link
        onClick={handleSwitchAccount}
        loading={switchingAccount}
        disabled={!isNil(pendingDecision)}
      >
        Use a different account
      </Button>
    </p>
  )

  const submitDecision = async (decisionName, decision) => {
    setDecisionError(null)
    setPendingDecision(decisionName)

    const { data, error } = await submitOAuthDecision(requestId, savedUser, decision)

    if (isNil(error)) {
      // The button keeps loading while the browser leaves, so the decision can't be sent twice.
      window.location.assign(data.redirect_to)
    } else if (error.kind === OAUTH_ERROR.SIGNED_OUT) {
      sendToLogin()
    } else if (PAGE_ERRORS.includes(error.kind)) {
      setPageError(error)
    } else {
      setDecisionError(error)
      setPendingDecision(null)
    }
  }

  const renderConsentForm = () => {
    const {
      app_name: appName,
      redirect_host: redirectHost,
      scopes,
      organizations,
      expires_at: expiresAt,
    } = consentRequest
    const canWrite = scopes.includes(OAUTH_SCOPE.WRITE)

    const handleAllow = () => {
      const grantedScopes =
        canWrite && allowWrite ? [OAUTH_SCOPE.READ, OAUTH_SCOPE.WRITE] : [OAUTH_SCOPE.READ]

      submitDecision(DECISION.ALLOW, { approve: true, scopes: grantedScopes })
    }

    const handleDeny = () => {
      submitDecision(DECISION.DENY, { approve: false })
    }

    return (
      <>
        <h2 data-testid="oauth-consent-page">Connect {appName} to Balto</h2>
        <div className="oauth-consent">
          <p>
            {appName} wants to act as you in Balto. It can only do what your role already allows.
          </p>
          {renderAccount()}

          <div className="flex-column small-gap">
            <p className="bold">{appName} will be able to:</p>
            <Checkbox label="View your Balto data" checked disabled data-testid="oauth-read" />
            {canWrite && (
              <Checkbox
                label="Make changes for you, like adding comments or editing playbooks"
                checked={allowWrite}
                disabled={!isNil(pendingDecision)}
                onChange={(event, { checked }) => setAllowWrite(checked)}
                data-testid="oauth-write"
              />
            )}
          </div>

          <div className="flex-column small-gap">
            <p className="bold">In these organizations:</p>
            <ul className="oauth-consent-organizations" data-testid="oauth-organizations">
              {organizations.map((organization) => (
                <li key={organization.id}>{organization.name}</li>
              ))}
            </ul>
          </div>

          <p>
            Access ends on {formatTime(expiresAt, EXPIRY_DATE_FORMAT)}. After that, you&apos;ll
            connect again.
          </p>
          <p className="muted-text">You&apos;ll go back to {redirectHost} after you choose.</p>

          {!isNil(decisionError) && (
            <ErrorMessage
              content={ERROR_MESSAGES[decisionError.kind]}
              data-testid="oauth-decision-error"
            />
          )}

          <div className="form-actions">
            <Button
              onClick={handleDeny}
              loading={pendingDecision === DECISION.DENY}
              disabled={pendingDecision === DECISION.ALLOW || switchingAccount}
            >
              Deny
            </Button>
            <Button
              brand
              onClick={handleAllow}
              loading={pendingDecision === DECISION.ALLOW}
              disabled={pendingDecision === DECISION.DENY || switchingAccount}
            >
              Allow
            </Button>
          </div>
        </div>
      </>
    )
  }

  const renderContent = () => {
    const pageErrorKind = invalidLink ? OAUTH_ERROR.INVALID_LINK : pageError?.kind

    if (!isNil(pageErrorKind)) {
      return (
        <div className="oauth-consent mt">
          <ErrorMessage content={ERROR_MESSAGES[pageErrorKind]} data-testid="oauth-page-error" />
          {pageErrorKind === OAUTH_ERROR.UNKNOWN && (
            <div className="form-actions">
              <Button brand onClick={handleRetry}>
                Try again
              </Button>
            </div>
          )}
          {ACCOUNT_ERRORS.includes(pageErrorKind) && renderAccount()}
        </div>
      )
    }

    if (isNil(consentRequest)) {
      return <Loader centered className="mt" data-testid="loading" />
    }

    return renderConsentForm()
  }

  return (
    <>
      <Helmet>
        <title>Connect an app | Balto Cloud</title>
      </Helmet>

      <AuthContainer>{renderContent()}</AuthContainer>
    </>
  )
}
